import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';
import { SearchInput } from '@ds';
import { AdminPageHeader, ConfirmationModal, DataTable } from '@ui';
import { toast } from 'react-toastify';
import {
  getRenderContractorsTableCell,
  getContractorsTableColumns,
  CONTRACTOR_LIST_FILTERS_BASE,
} from '@lp/features/admin/tables/ContractorsListTable';
import { ContractorFilters } from '@lp/features/admin/tables/ContractorsListTable/components/ContractorFilters';
import { AuthContext } from '@lp/core/auth';
import { EmployeeComposed } from '@/api/apis/resources';
import { useActivateEmployeeMutation, useDeactivateEmployeeMutation, useEmployeesListQuery } from '@/queries/employee';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

export const ContractorsList = () => {
  const { resolveSubdivisionName } = useCountriesData();
  const [employeeToChange, setEmployeeToChange] = useState<EmployeeComposed | undefined>();
  const { mutateAsync: deactivateAsync } = useDeactivateEmployeeMutation();
  const { mutateAsync: activateAsync } = useActivateEmployeeMutation();
  const { checkPermission } = useContext(AuthContext);

  const hasEmployeeEditPermission = checkPermission(AdminPermissions.TmdxUsersUpdate);

  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: { ...CONTRACTOR_LIST_FILTERS_BASE },
    query: useEmployeesListQuery,
  });

  const confirmationModalTitle = `${employeeToChange && employeeToChange.isActive ? 'Deactivate' : 'Activate'} TMDX Contractor?`;
  const confirmationModalDesc = `Are you sure you want to ${employeeToChange && employeeToChange.isActive ? 'deactivate' : 'activate'} the TMDX Contractor?`;
  const confirmationModalOpen = !!employeeToChange;

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const onIsActiveSwitchChanged = useCallback((employee: EmployeeComposed) => {
    setEmployeeToChange(employee);
  }, []);

  const onChangeActive = async () => {
    if (!employeeToChange) {
      toast.error(`No contractor selected`);
      return;
    }

    try {
      if (employeeToChange.isActive) {
        await deactivateAsync(employeeToChange.id);
      } else {
        await activateAsync(employeeToChange.id);
      }
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const onConfirmationModalClose = useCallback(() => {
    setEmployeeToChange(undefined);
  }, []);

  const TABLE_COLUMNS = useMemo(() => getContractorsTableColumns(), []);

  const renderTableCell = useMemo(
    () => getRenderContractorsTableCell(onIsActiveSwitchChanged, hasEmployeeEditPermission, resolveSubdivisionName),
    [hasEmployeeEditPermission, onIsActiveSwitchChanged, resolveSubdivisionName],
  );

  return (
    <div className="flex flex-col place-items-stretch gap-9">
      <AdminPageHeader title="Contractors">
        <div className="max-w-[480px] grow">
          <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
        </div>
      </AdminPageHeader>
      <div className="flex flex-col gap-4">
        <ContractorFilters
          filters={filters}
          hasFilters={hasFilters}
          updateFilters={updateFilters}
          clearFilters={clearFilters}
        />
        <DataTable
          label="Contractors"
          columns={TABLE_COLUMNS}
          renderCell={renderTableCell}
          data={data?.items || []}
          isLoading={isLoading}
          isFetching={isFetching}
          totalItems={data?.totalItems || 0}
          totalPages={data?.totalPages || 1}
          page={page}
          itemsPerPage={itemsPerPage}
          sortBy={sortBy}
          onPageChange={setPage}
          onItemsPerPageChange={setItemsPerPage}
          onSortByChange={setSortBy}
        />
      </div>
      <ConfirmationModal
        title={confirmationModalTitle}
        description={confirmationModalDesc}
        isOpen={confirmationModalOpen}
        onChangeActive={onChangeActive}
        onClose={onConfirmationModalClose}
      />
    </div>
  );
};
